import {
  DndContext,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useDndContext,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  ArticleIndexEntry,
  KnowledgeBaseCategory,
} from "knowledge-base/_api/knowledge-base-api";
import {
  categoryBeforeId,
  categoryDragId,
  dropEdgeFor,
  isCategoryDrag,
  resolveCategoryDropIntent,
  resolveDropIntent,
  TOP_LEVEL_DROP_ID,
  UNFILED_DROP_ID,
  type OrderArticlesIntent,
  type OrderCategoriesIntent,
} from "knowledge-base/articles/_views/article-filing";
import { useKnowledgeBaseLocations } from "knowledge-base/knowledge-base-locations";
import { useState } from "react";
import { Link as RouterLink } from "react-router";

export type CategoryNode = KnowledgeBaseCategory & {
  children: CategoryNode[];
  articles: ArticleIndexEntry[];
};

export type IndexTree = Readonly<{
  categories: CategoryNode[];
  /**
   * Articles that are not filed anywhere. Shown after the categories rather than hidden — an upload
   * the agent could not place has to be findable, or nobody will ever file it.
   */
  unfiled: ArticleIndexEntry[];
}>;

const byPosition = <T extends { position: number }>(
  left: T & { name?: string; title?: string },
  right: T & { name?: string; title?: string }
) =>
  left.position - right.position ||
  (left.name ?? left.title ?? "").localeCompare(right.name ?? right.title ?? "");

/**
 * Assembles the navigation tree from the two lists the API returns.
 *
 * Categories nest; articles are leaves hanging off whichever category they were filed in. An
 * article whose category is missing from the response is treated as unfiled rather than dropped —
 * the category may have been removed, or belong to a library this reader cannot see, and losing
 * the article because of where someone put it would be worse than showing it loose.
 */
export function buildIndexTree(
  categories: readonly KnowledgeBaseCategory[],
  entries: readonly ArticleIndexEntry[]
): IndexTree {
  const nodesById = new Map<string, CategoryNode>(
    categories.map((category) => [category.id, { ...category, children: [], articles: [] }])
  );

  const roots: CategoryNode[] = [];
  for (const node of nodesById.values()) {
    const parent = node.parentCategoryId ? nodesById.get(node.parentCategoryId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const unfiled: ArticleIndexEntry[] = [];
  for (const entry of entries) {
    const category = entry.categoryId ? nodesById.get(entry.categoryId) : undefined;
    if (category) {
      category.articles.push(entry);
    } else {
      unfiled.push(entry);
    }
  }

  const sortDeep = (nodes: CategoryNode[]) => {
    nodes.sort(byPosition);
    nodes.forEach((node) => {
      node.articles.sort(byPosition);
      sortDeep(node.children);
    });
  };
  sortDeep(roots);
  unfiled.sort(byPosition);

  return { categories: roots, unfiled };
}

function containsSlug(node: CategoryNode, slug?: string): boolean {
  if (!slug) return false;
  if (node.articles.some((article) => article.slug === slug)) return true;
  return node.children.some((child) => containsSlug(child, slug));
}

/**
 * Which categories the reader has opened or closed by hand.
 *
 * Only the deviations are held, not the whole state of the tree. Everything unnamed follows the
 * default below, which means a tree that grows — a new category, an article filed somewhere — needs
 * no reconciling, and "expand all" is simply every id named at once.
 */
type ExpansionOverrides = ReadonlyMap<string, boolean>;

/**
 * Open when the reader says so, and otherwise open at the top level and along the branch holding
 * the article being read. That second rule is what makes a deep link land somewhere the reader can
 * see where they are.
 */
function isExpanded(
  node: CategoryNode,
  depth: number,
  overrides: ExpansionOverrides,
  activeSlug?: string
): boolean {
  return overrides.get(node.id) ?? (depth === 0 || containsSlug(node, activeSlug));
}

function collectCategoryIds(categories: readonly CategoryNode[]): string[] {
  return categories.flatMap((category) => [
    category.id,
    ...collectCategoryIds(category.children),
  ]);
}

type ArticleRowProps = Readonly<{
  entry: ArticleIndexEntry;
  activeSlug?: string;
  depth: number;
  filingEnabled: boolean;
  /** The list this row belongs to, in display order, so the row can work out which edge to mark. */
  siblings: readonly ArticleIndexEntry[];
}>;

function ArticleRow({ entry, activeSlug, depth, filingEnabled, siblings }: ArticleRowProps) {
  const isSummary = entry.origin === "AgentSummarized";
  // Built from the address this tree is being shown at, so an article in Attorney Hub's library
  // opens there rather than in the reader's own knowledge base.
  const { articlePath } = useKnowledgeBaseLocations();

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: entry.id,
    disabled: !filingEnabled,
  });

  // Also a drop target, which is what makes reordering possible: dropping one article on another
  // says "put it here", where dropping on a category heading only says "put it in there".
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: entry.id,
    disabled: !filingEnabled || isDragging,
  });

  // Which side the incoming article will land on, worked out with the same rule the drop uses, so
  // the line never promises one thing and the drop does another.
  const { active } = useDndContext();
  const edge = isOver && active ? dropEdgeFor(String(active.id), entry.id, siblings) : null;

  return (
    <ListItemButton
      component={RouterLink}
      ref={(node: HTMLElement | null) => {
        setNodeRef(node);
        setDropRef(node);
      }}
      selected={entry.slug === activeSlug}
      sx={{
        // A line at the boundary rather than a filled highlight: the article lands beside this row,
        // not inside it, and a block colour would suggest otherwise.
        borderBottom: "2px solid transparent",
        borderBottomColor: edge === "bottom" ? "primary.main" : "transparent",
        borderTop: "2px solid transparent",
        borderTopColor: edge === "top" ? "primary.main" : "transparent",
        opacity: isDragging ? 0.4 : 1,
        pl: 2 + depth * 2,
      }}
      to={articlePath(entry.slug)}
    >
      {filingEnabled && (
        // A handle rather than making the whole row draggable. The row is a link, and a link that
        // is also a drag source makes both worse: a slightly-moved click stops navigating, and a
        // drag that starts on the text competes with selecting it.
        <Box
          {...listeners}
          {...attributes}
          aria-label={`Move ${entry.title} to another category`}
          component="span"
          onClick={(event) => event.preventDefault()}
          sx={{
            alignItems: "center",
            color: "text.disabled",
            cursor: "grab",
            display: "flex",
            mr: 0.5,
            touchAction: "none",
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      <ListItemIcon sx={{ minWidth: 36 }}>
        {entry.templateKey ? (
          <EmailOutlinedIcon fontSize="small" />
        ) : (
          <ArticleOutlinedIcon fontSize="small" />
        )}
      </ListItemIcon>
      <ListItemText primary={entry.title} slotProps={{ primary: { variant: "body2" } }} />
      {isSummary && (
        <Tooltip title="Summarized from an uploaded document and not yet checked">
          <AutoAwesomeIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
        </Tooltip>
      )}
      {entry.publication === "Draft" && (
        <Chip label="Draft" size="small" sx={{ mr: 1 }} variant="outlined" />
      )}
    </ListItemButton>
  );
}

/**
 * A thin target above a category row that means "beside this one, not inside it".
 *
 * Only present while a category is being dragged. The rest of the time it would sit between every
 * row competing with the article drop targets, and there is nothing an article could do with it.
 */
function SiblingDropStrip({
  categoryId,
  depth,
}: Readonly<{ categoryId: string; depth: number }>) {
  const { active } = useDndContext();
  const draggingACategory = active ? isCategoryDrag(String(active.id)) : false;

  const { isOver, setNodeRef } = useDroppable({
    id: categoryBeforeId(categoryId),
    disabled: !draggingACategory,
  });

  if (!draggingACategory) {
    return null;
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        bgcolor: isOver ? "primary.main" : "transparent",
        height: 4,
        ml: 2 + depth * 2,
        transition: "background-color 120ms",
      }}
    />
  );
}

/**
 * The target at the end of the tree that lifts a nested category back out to the top level.
 */
function TopLevelDropStrip() {
  const { active } = useDndContext();
  const draggingACategory = active ? isCategoryDrag(String(active.id)) : false;

  const { isOver, setNodeRef } = useDroppable({
    id: TOP_LEVEL_DROP_ID,
    disabled: !draggingACategory,
  });

  if (!draggingACategory) {
    return null;
  }

  return (
    <Box
      ref={setNodeRef}
      sx={{
        border: "1px dashed",
        borderColor: isOver ? "primary.main" : "divider",
        borderRadius: 1,
        color: isOver ? "primary.main" : "text.disabled",
        fontSize: 12,
        mt: 1,
        mx: 2,
        py: 0.75,
        textAlign: "center",
      }}
    >
      Move to top level
    </Box>
  );
}

type CategoryBranchProps = Readonly<{
  node: CategoryNode;
  activeSlug?: string;
  depth: number;
  onEditCategory?: (category: KnowledgeBaseCategory) => void;
  filingEnabled: boolean;
  /**
   * False when this is the only category there is. Nothing to reorder against and nothing to nest
   * under, so the handle would be an affordance for a move that cannot be made.
   */
  categoriesAreMovable: boolean;
  expansion: ExpansionOverrides;
  onToggleCategory: (categoryId: string, expanded: boolean) => void;
}>;

function CategoryBranch({
  node,
  activeSlug,
  depth,
  onEditCategory,
  filingEnabled,
  categoriesAreMovable,
  expansion,
  onToggleCategory,
}: CategoryBranchProps) {
  const expanded = isExpanded(node, depth, expansion, activeSlug);
  const hasContents = node.children.length > 0 || node.articles.length > 0;
  const draggable = filingEnabled && categoriesAreMovable;

  const { isOver, setNodeRef } = useDroppable({ id: node.id, disabled: !filingEnabled });

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: categoryDragId(node.id), disabled: !draggable });

  return (
    <>
      <SiblingDropStrip categoryId={node.id} depth={depth} />
      <ListItemButton
        onClick={() => onToggleCategory(node.id, !expanded)}
        ref={(element: HTMLElement | null) => {
          setNodeRef(element);
          setDragRef(element);
        }}
        sx={{
          bgcolor: isOver ? "action.selected" : undefined,
          opacity: isDragging ? 0.4 : 1,
          outline: isOver ? "2px solid" : undefined,
          outlineColor: "primary.main",
          pl: 2 + depth * 2,
        }}
      >
        {draggable && (
          <Box
            {...listeners}
            {...attributes}
            aria-label={`Move the ${node.name} category`}
            component="span"
            onClick={(event) => event.stopPropagation()}
            sx={{
              alignItems: "center",
              color: "text.disabled",
              cursor: "grab",
              display: "flex",
              mr: 0.5,
              touchAction: "none",
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <ListItemIcon sx={{ minWidth: 36 }}>
          <FolderOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={node.name}
          secondary={node.description ?? undefined}
          slotProps={{
            primary: { variant: "subtitle2" },
            secondary: { variant: "caption" },
          }}
        />
        {node.origin === "AgentCreated" && (
          <Tooltip title="Created automatically when an upload fitted nowhere else">
            <AutoAwesomeIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
          </Tooltip>
        )}
        {onEditCategory && (
          <Tooltip title="Rename this category, or change what belongs in it">
            <IconButton
              aria-label={`Edit ${node.name}`}
              edge="end"
              onClick={(event) => {
                // The row toggles the section; the pencil does not. Without stopping the event here
                // the section would fold shut behind the dialog every time.
                event.stopPropagation();
                onEditCategory(node);
              }}
              size="small"
              sx={{ mr: 0.5 }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {hasContents && (expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
      </ListItemButton>

      {hasContents && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => (
              <CategoryBranch
                activeSlug={activeSlug}
                categoriesAreMovable={categoriesAreMovable}
                depth={depth + 1}
                expansion={expansion}
                filingEnabled={filingEnabled}
                key={child.id}
                node={child}
                onEditCategory={onEditCategory}
                onToggleCategory={onToggleCategory}
              />
            ))}
            {node.articles.map((entry) => (
              <ArticleRow
                activeSlug={activeSlug}
                depth={depth + 1}
                entry={entry}
                filingEnabled={filingEnabled}
                key={entry.id}
                siblings={node.articles}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

/**
 * Everything filed nowhere, and the place an article goes to stop being in any category.
 *
 * Present whenever it holds something. Otherwise it appears only while an article is being dragged,
 * because it is the only way to take one back out of a category: a heading that showed up empty and
 * stayed there was noise, and one that never showed up at all would strand every filed article.
 */
function UnfiledSection({
  activeSlug,
  filingEnabled,
  unfiled,
}: Readonly<{
  activeSlug?: string;
  filingEnabled: boolean;
  unfiled: readonly ArticleIndexEntry[];
}>) {
  const { active } = useDndContext();
  const draggingAnArticle = active ? !isCategoryDrag(String(active.id)) : false;

  if (unfiled.length === 0 && !(filingEnabled && draggingAnArticle)) {
    return null;
  }

  return (
    <>
      <UnfiledDropZone filingEnabled={filingEnabled} />
      {unfiled.map((entry) => (
        <ArticleRow
          activeSlug={activeSlug}
          depth={0}
          entry={entry}
          filingEnabled={filingEnabled}
          key={entry.id}
          siblings={unfiled}
        />
      ))}
    </>
  );
}

function UnfiledDropZone({ filingEnabled }: Readonly<{ filingEnabled: boolean }>) {
  const { isOver, setNodeRef } = useDroppable({
    id: UNFILED_DROP_ID,
    disabled: !filingEnabled,
  });

  return (
    <ListItemButton
      disableRipple
      ref={setNodeRef}
      sx={{
        bgcolor: isOver ? "action.selected" : undefined,
        cursor: "default",
        outline: isOver ? "2px solid" : undefined,
        outlineColor: "primary.main",
        pl: 2,
        pt: 2,
      }}
    >
      <ListItemText primary="Not filed" slotProps={{ primary: { variant: "overline" } }} />
    </ListItemButton>
  );
}

/**
 * Folding the whole tree at once, kept in a menu.
 *
 * Two buttons sitting permanently above the contents were two more things to read every time
 * someone glanced at the panel, for an action taken rarely. A menu costs the one extra click on the
 * rare occasion and nothing the rest of the time.
 */
function FoldingMenu({
  onFoldEverything,
}: Readonly<{ onFoldEverything: (expanded: boolean) => void }>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const fold = (expanded: boolean) => {
    onFoldEverything(expanded);
    setAnchor(null);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1 }}>
      <Tooltip title="Expand or collapse categories">
        <IconButton
          aria-label="Expand or collapse categories"
          onClick={(event) => setAnchor(event.currentTarget)}
          size="small"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchor} onClose={() => setAnchor(null)} open={Boolean(anchor)}>
        <MenuItem onClick={() => fold(true)}>
          <ListItemIcon>
            <UnfoldMoreIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Expand all categories" />
        </MenuItem>
        <MenuItem onClick={() => fold(false)}>
          <ListItemIcon>
            <UnfoldLessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Collapse all categories" />
        </MenuItem>
      </Menu>
    </Box>
  );
}

type Props = Readonly<{
  categories: readonly KnowledgeBaseCategory[];
  entries: readonly ArticleIndexEntry[];
  activeSlug?: string;
  onEditCategory?: (category: KnowledgeBaseCategory) => void;
  /**
   * Files an article into a category, or out of every category. Absent for a reader who may not
   * write here — a firm reading its client's rules can open them and nothing else.
   */
  onFileArticle?: (intent: OrderArticlesIntent) => void;
  /**
   * Reorders or re-parents categories. Absent for a reader who may not write here.
   */
  onOrderCategories?: (intent: OrderCategoriesIntent) => void;
}>;

export default function KnowledgeBaseIndexTree({
  categories,
  entries,
  activeSlug,
  onEditCategory,
  onFileArticle,
  onOrderCategories,
}: Props) {
  const tree = buildIndexTree(categories, entries);
  const isEmpty = tree.categories.length === 0 && tree.unfiled.length === 0;
  const filingEnabled = Boolean(onFileArticle);

  const [expansion, setExpansion] = useState<ExpansionOverrides>(new Map());

  const toggleCategory = (categoryId: string, expanded: boolean) =>
    setExpansion((current) => new Map(current).set(categoryId, expanded));

  const categoryIds = collectCategoryIds(tree.categories);

  const setEveryCategory = (expanded: boolean) =>
    setExpansion(new Map(categoryIds.map((id) => [id, expanded])));

  const categoriesAreMovable = categoryIds.length > 1;
  // Nothing to fold when no category holds anything, and a pair of buttons that do nothing reads
  // as a broken control rather than an empty tree.
  const anythingFolds = tree.categories.some(
    (node) => node.children.length > 0 || node.articles.length > 0
  );

  // A small distance before a drag begins, so that a plain click on a drag handle still behaves
  // like a click rather than starting a drag nobody asked for. The keyboard sensor is what makes
  // filing reachable without a pointer at all.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;

    // What is being dragged decides which set of rules applies. The same category row is a target
    // for both: an article files into it, a category nests inside it.
    if (isCategoryDrag(activeId)) {
      if (!onOrderCategories) return;

      const intent = resolveCategoryDropIntent(activeId, overId, tree);
      if (intent) onOrderCategories(intent);
      return;
    }

    if (!onFileArticle) return;

    const intent = resolveDropIntent(activeId, overId, tree);
    if (intent) onFileArticle(intent);
  };

  if (isEmpty) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary" variant="body2">
          Nothing here yet.
        </Typography>
      </Box>
    );
  }

  return (
    <DndContext
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up ${describe(tree, String(active.id))}.`,
          onDragOver: ({ over }) =>
            over
              ? `Over ${describeTarget(tree, String(over.id))}.`
              : "Not over a category.",
          onDragEnd: ({ over }) =>
            over
              ? `Filed under ${describeTarget(tree, String(over.id))}.`
              : "Dropped outside a category. Nothing moved.",
          onDragCancel: () => "Cancelled. Nothing moved.",
        },
        screenReaderInstructions: {
          draggable:
            "Press space or enter to pick this article up. Use the arrow keys to choose a "
            + "category, then press space or enter again to file it there. Press escape to cancel.",
        },
      }}
      // The unfiled heading appears part-way through a drag, so droppables are measured as they
      // arrive rather than once at the start. Without this the zone would be visible and inert.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      {anythingFolds && <FoldingMenu onFoldEverything={setEveryCategory} />}

      <List aria-label="Knowledge base contents" component="nav" dense>
        {tree.categories.map((node) => (
          <CategoryBranch
            activeSlug={activeSlug}
            categoriesAreMovable={categoriesAreMovable}
            depth={0}
            expansion={expansion}
            filingEnabled={filingEnabled}
            key={node.id}
            node={node}
            onEditCategory={onEditCategory}
            onToggleCategory={toggleCategory}
          />
        ))}

        <TopLevelDropStrip />

        <UnfiledSection
          activeSlug={activeSlug}
          filingEnabled={filingEnabled}
          unfiled={tree.unfiled}
        />
      </List>
    </DndContext>
  );
}

/**
 * Names an article for a screen reader announcement.
 */
function describe(tree: IndexTree, activeId: string): string {
  if (isCategoryDrag(activeId)) {
    const categoryId = activeId.slice("category:".length);
    return findCategoryNode(tree.categories, categoryId)?.name ?? "this category";
  }

  const all = [...tree.unfiled, ...collectArticles(tree.categories)];
  return all.find((article) => article.id === activeId)?.title ?? "this article";
}

/**
 * Names a drop target for a screen reader announcement.
 */
function describeTarget(tree: IndexTree, overId: string): string {
  if (overId === UNFILED_DROP_ID) return "Not filed";
  if (overId === TOP_LEVEL_DROP_ID) return "the top level";

  if (overId.startsWith("before-category:")) {
    const targetId = overId.slice("before-category:".length);
    const before = findCategoryNode(tree.categories, targetId);
    return before ? `before ${before.name}` : "that position";
  }

  const found = findCategoryNode(tree.categories, overId);
  return found?.name ?? "that category";
}

function collectArticles(categories: readonly CategoryNode[]): ArticleIndexEntry[] {
  return categories.flatMap((category) => [
    ...category.articles,
    ...collectArticles(category.children),
  ]);
}

function findCategoryNode(
  categories: readonly CategoryNode[],
  categoryId: string
): CategoryNode | undefined {
  for (const category of categories) {
    if (category.id === categoryId) return category;

    const nested = findCategoryNode(category.children, categoryId);
    if (nested) return nested;
  }

  return undefined;
}
